import React, { useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import pilarImg from '../Assets/bg/Pilar.jpg';
import sanMiguelImg from '../Assets/bg/SanMiguel.jpg';
import capayasImg from '../Assets/bg/Capayas.jpg';
import ubayImg from '../Assets/bg/Ubay.jpg';
import backgroundImage from '../Assets/template/overview.png';

const Overview = () => {
  const sliderRef = useRef(null);

  const settings = {
    dots: true,
    arrows: false,
    infinite: true, // Changed to true for continuous looping
    speed: 1300,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    draggable: true,
    fade: true,
    cssEase: 'ease-in-out',
    autoplay: true,
    autoplaySpeed: 3000, // Time between slides (in milliseconds)
  };

  const slides = [
    {
      title: 'OVERVIEW OF DISA',
      content: 'The DISA (Digital Irrigator System Assessment) is designed to optimize and improve the efficiency of information flow across systems. It integrates multiple technologies to support automation, enhance decision-making, and provide valuable insights through data analysis.',
    },
    {
      title: 'Key Objectives of DISA',
      content: [
        'Efficiency and Automation: Automating repetitive tasks and processes to save time and reduce errors.',
        'Data Integration: Integrating disparate data sources into a cohesive system for better insights.',
        'Real-time Analytics: Providing real-time data analysis for faster decision-making and forecasting.',
      ],
    },
    {
      title: 'Key Features',
      content: [
        'Centralized Data Management: A single platform that manages all incoming and outgoing data streams.',
        'Advanced Security Protocols: Ensuring all data is encrypted and securely transmitted.',
        'Modular Architecture: Scalable and customizable to fit the needs of various departments.',
      ],
    },
    {
      title: 'Impact on Operations',
      content: [
        'Improved Collaboration: Teams can seamlessly share data and collaborate.',
        'Faster Decision-Making: Real-time analytics enable informed decisions.',
        'Cost Efficiency: Operational inefficiencies are reduced, saving costs.',
      ],
    },
    {
      title: 'Future Development',
      content: [
        'AI Integration: Future versions will incorporate AI for better predictions.',
        'Cloud Expansion: Moving to cloud for scalability and accessibility.',
      ],
    },
  ];

  return (
    <Wrapper>
      <NewsSection>
        <NewsTitle>LATEST NEWS</NewsTitle>
        <NewsImages>
          <NewsItem><img src={pilarImg} alt="Pilar" /><span>PILAR</span></NewsItem>
          <NewsItem><img src={sanMiguelImg} alt="San Miguel" /><span>SAN MIGUEL</span></NewsItem>
          <NewsItem><img src={capayasImg} alt="Capayas" /><span>CAPAYAS</span></NewsItem>
          <NewsItem><img src={ubayImg} alt="Ubay" /><span>UBAY</span></NewsItem>
        </NewsImages>
      </NewsSection>

      <OverviewSection>
        <SliderWrapper>
          <Slider ref={sliderRef} {...settings}>
            {slides.map((slide, index) => (
              <FadeIn key={index}>
                <SlideCard background={backgroundImage}>
                  <Subtitle>{slide.title}</Subtitle>
                  {Array.isArray(slide.content) ? (
                    <ScrollableContent>
                      <List>
                        {slide.content.map((item, i) => (
                          <ListItem key={i}>
                            <strong>{item.split(':')[0]}:</strong>
                            {item.includes(':') ? item.split(':')[1] : ''}
                          </ListItem>
                        ))}
                      </List>
                    </ScrollableContent>
                  ) : (
                    <Paragraph>{slide.content}</Paragraph>
                  )}
                </SlideCard>
              </FadeIn>
            ))}
          </Slider>
        </SliderWrapper>
      </OverviewSection>
    </Wrapper>
  );
};

export default Overview;

// Styled Components & Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const FadeIn = styled.div`
  animation: ${fadeIn} 0.8s ease forwards;
`;

const Wrapper = styled.div`
  font-family: 'Segoe UI', sans-serif;
  background: #f1f1f1;
  padding: 0 16px 40px;
`;

const NewsSection = styled.div`
  background: white;
  padding: 24px;
  border-bottom: 1px solid #ccc;
  width: 100%;
  margin: 0 auto;
  max-width: 1200px;
`;

const NewsTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #228B22;
  margin-bottom: 20px;
  text-align: center;
`;

const NewsImages = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 24px;
`;

const NewsItem = styled.div`
  position: relative;
  overflow: hidden;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  padding: 15px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  will-change: transform;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);

    img {
      transform: scale(1.08);
      filter: brightness(0.85);
    }

    span {
      opacity: 1;
      transform: translate(-50%, 0);
    }
  }

  img {
    width: 100%;
    height: 150px;
    object-fit: cover;
    border-radius: 8px;
    transition: transform 0.3s ease, filter 0.3s ease;
    will-change: transform, filter;
  }

  span {
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translate(-50%, 10px);
    background: rgba(34, 134, 34, 0.85);
    color: white;
    padding: 6px 12px;
    border-radius: 6px;
    font-weight: 600;
    font-size: 14px;
    opacity: 0;
    transition: opacity 0.25s ease, transform 0.25s ease;
    pointer-events: none;
  }
`;

const OverviewSection = styled.section`
  margin: 40px auto 0;
  padding: 40px 30px;
  max-width: 1100px;
  border-radius: 15px;
`;

const SliderWrapper = styled.div`
  .slick-slide {
    padding: 15px;
  }
  .slick-dots {
    bottom: -10px;
  }
`;

const SlideCard = styled.section`
  max-width: 1000px;
  margin: 0 auto;
  padding: 30px;
  border-radius: 16px;
  background-image: ${({ background }) => `url(${background})`};
  background-size: cover;
  background-position: center;
  background-blend-mode: overlay;
  background-color: rgba(9, 81, 9, 0.75);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  box-shadow:
    0 6px 12px rgba(49, 49, 49, 0.1),
    0 12px 20px rgba(0, 0, 0, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow:
      0 10px 20px rgba(0, 0, 0, 0.4),
      0 16px 28px rgba(0, 0, 0, 0.10);
  }
`;

const Subtitle = styled.h2`
  font-size: 32px;
  font-weight: 900; /* Already bold */
  color: white; /* Changed to white */
  margin-bottom: 15px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.5px;
`;

const Paragraph = styled.p`
  font-size: 18px;
  background: rgba(255, 255, 255, 0.85);
  padding: 15px;
  border-radius: 10px;
  color: #333;
  line-height: 1.8;
`;

const ScrollableContent = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding-right: 10px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #ccc;
    border-radius: 4px;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ListItem = styled.li`
  font-size: 16px;
  background: rgba(255, 255, 255, 0.95);
  color: #333;
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  transition: background 0.3s ease;

  strong {
    color: #228B22;
  }

  &:hover {
    background-color: #d2f2e4;
  }
`;